import { useEffect, useId, useRef, useState } from 'react';
import { languageLabel, searchLanguages } from '../editor/languages';

interface LanguagePickerProps {
  value: string;
  onChange: (language: string) => void;
  disabled?: boolean;
}

/**
 * Searchable combobox over every supported language.
 *
 * Keyboard-navigable by design: arrows move, Enter picks, Escape closes. The listbox follows the
 * ARIA combobox pattern so the active option is announced rather than only highlighted.
 */
export function LanguagePicker({ value, onChange, disabled = false }: LanguagePickerProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);

  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listId = useId();

  const matches = searchLanguages(query);

  useEffect(() => {
    if (!open) {
      return;
    }

    function onPointerDown(event: PointerEvent) {
      if (!containerRef.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    }

    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [open]);

  useEffect(() => {
    setActiveIndex(0);
  }, [query]);

  // Focus in an effect, not a requestAnimationFrame callback. rAF fires on the next paint, so
  // whether it beats the user's next keystroke depends on how busy the machine is — fine on a
  // developer laptop, not on a loaded CI runner, where the first characters typed went nowhere
  // and the list never filtered. An effect runs as part of the same commit as the render that
  // created the input.
  useEffect(() => {
    if (open) {
      inputRef.current?.focus();
    }
  }, [open]);

  function choose(id: string) {
    onChange(id);
    setOpen(false);
    setQuery('');
  }

  function openPicker() {
    if (disabled) {
      return;
    }

    setOpen(true);
    setQuery('');
  }

  return (
    <div ref={containerRef} className="relative">
      {!open ? (
        <button
          type="button"
          disabled={disabled}
          onClick={openPicker}
          aria-label={`Language: ${languageLabel(value)}. Change language`}
          className="rounded-md border border-border-subtle bg-surface px-2 py-1 text-xs
            text-text-muted transition-colors hover:text-text disabled:opacity-50"
        >
          {languageLabel(value)}
        </button>
      ) : (
        <>
          <input
            ref={inputRef}
            role="combobox"
            aria-expanded="true"
            aria-controls={listId}
            aria-autocomplete="list"
            aria-activedescendant={
              matches[activeIndex] ? `${listId}-${matches[activeIndex].id}` : undefined
            }
            aria-label="Search languages"
            placeholder="Search languages…"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'ArrowDown') {
                event.preventDefault();
                setActiveIndex((index) => Math.min(index + 1, matches.length - 1));
              } else if (event.key === 'ArrowUp') {
                event.preventDefault();
                setActiveIndex((index) => Math.max(index - 1, 0));
              } else if (event.key === 'Enter') {
                event.preventDefault();
                const picked = matches[activeIndex];
                if (picked) {
                  choose(picked.id);
                }
              } else if (event.key === 'Escape') {
                event.preventDefault();
                setOpen(false);
              }
            }}
            className="w-44 rounded-md border border-border-subtle bg-surface px-2 py-1 text-xs text-text"
          />

          <ul
            id={listId}
            role="listbox"
            aria-label="Languages"
            className="absolute right-0 z-20 mt-1 max-h-64 w-56 overflow-auto rounded-md border
              border-border-subtle bg-surface-raised py-1 shadow-lg"
          >
            {matches.length === 0 && (
              <li className="px-3 py-2 text-xs text-text-muted">No language matches that.</li>
            )}

            {matches.map((language, index) => (
              <li
                key={language.id}
                id={`${listId}-${language.id}`}
                role="option"
                aria-selected={language.id === value}
                onPointerDown={(event) => {
                  event.preventDefault();
                  choose(language.id);
                }}
                onPointerEnter={() => setActiveIndex(index)}
                className={`cursor-pointer px-3 py-1.5 text-xs
                  ${index === activeIndex ? 'bg-accent-subtle text-text' : 'text-text-muted'}`}
              >
                {language.label}
                {language.id === value && <span className="ml-2 text-accent">✓</span>}
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}
